import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import recommendationService from '../../services/recommendation.service';
import { useLanguage } from '../../context/LanguageContext';

/**
 * ProductRecommendations
 * Props:
 *   type        - 'also-bought' | 'similar' | 'trending' | 'for-you'
 *   productId   - required for also-bought and similar
 *   viewedIds   - array of product IDs for for-you
 *   title       - custom title override
 *   icon        - emoji/icon prefix
 *   limit       - max products
 *   category    - for trending filter
 */
const ProductRecommendations = ({ type = 'trending', productId, viewedIds, title, icon, limit = 6, category }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const { lang, t } = useLanguage();

  const defaultTitles = {
    'also-bought': t('relatedProducts'),
    'similar': t('relatedProducts'),
    'trending': t('trendingProducts'),
    'for-you': t('featuredProducts'),
  };

  const defaultIcons = {
    'also-bought': '🛒',
    'similar': '✨',
    'trending': '🔥',
    'for-you': '💡',
  };

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        let res;
        switch (type) {
          case 'also-bought':
            if (!productId) return;
            res = await recommendationService.alsoBought(productId, limit);
            break;
          case 'similar':
            if (!productId) return;
            res = await recommendationService.similar(productId, limit);
            break;
          case 'for-you':
            res = await recommendationService.forYou(viewedIds || [], limit);
            break;
          case 'trending':
          default:
            res = await recommendationService.trending(limit, category);
        }
        setProducts(res.data.data || []);
      } catch { setProducts([]); }
      setLoading(false);
    };
    fetch();
  }, [type, productId, category, lang]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 260, behavior: 'smooth' });
    }
  };

  if (loading) return null;
  if (!products.length) return null;

  const displayTitle = title || defaultTitles[type] || 'Recommended';
  const displayIcon = icon || defaultIcons[type] || '⭐';

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 style={{ fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>{displayIcon}</span> {displayTitle}
        </h5>
        <div className="d-flex gap-1">
          <button onClick={() => scroll(-1)} className="btn btn-sm btn-outline-secondary rounded-circle"
            style={{ width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiChevronLeft size={16} />
          </button>
          <button onClick={() => scroll(1)} className="btn btn-sm btn-outline-secondary rounded-circle"
            style={{ width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Scrollable row */}
      <div ref={scrollRef} style={{
        display: 'flex', gap: '14px', overflowX: 'auto', scrollSnapType: 'x mandatory',
        paddingBottom: '8px', msOverflowStyle: 'none', scrollbarWidth: 'none',
      }}>
        <style>{`.rec-scroll::-webkit-scrollbar { display: none; }`}</style>
        {products.map(p => {
          const img = p.images?.[0]?.url;
          const discount = p.compareAtPrice && p.compareAtPrice > p.price
            ? Math.round(((p.compareAtPrice - p.price) / p.compareAtPrice) * 100)
            : 0;

          return (
            <Link
              key={p._id} to={`/products/${p.slug}`}
              style={{
                flex: '0 0 180px', scrollSnapAlign: 'start',
                textDecoration: 'none', color: 'inherit',
                background: '#FFF', borderRadius: '10px', overflow: 'hidden',
                border: '1px solid #E5E7EB', transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ position: 'relative', paddingBottom: '100%', background: '#F9FAFB' }}>
                {img ? (
                  <img src={img} alt={p.name}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#D1D5DB' }}>
                    No Image
                  </div>
                )}
                {discount > 0 && (
                  <span style={{
                    position: 'absolute', top: '6px', left: '6px',
                    background: '#EF4444', color: '#FFF', borderRadius: '4px',
                    padding: '1px 6px', fontSize: '10px', fontWeight: 800,
                  }}>-{discount}%</span>
                )}
              </div>

              <div style={{ padding: '8px 10px' }}>
                <p style={{
                  fontSize: '12px', fontWeight: 600, color: '#374151', margin: '0 0 4px',
                  lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>{p.name}</p>
                <div className="d-flex align-items-center gap-1">
                  <span style={{ fontWeight: 800, fontSize: '14px', color: '#B91C1C' }}>${p.price?.toFixed(2)}</span>
                  {p.compareAtPrice > p.price && (
                    <span style={{ fontSize: '11px', color: '#9CA3AF', textDecoration: 'line-through' }}>${p.compareAtPrice?.toFixed(2)}</span>
                  )}
                </div>
                {p.ratings?.average > 0 && (
                  <div style={{ fontSize: '10px', color: '#F59E0B', marginTop: '2px' }}>
                    {'★'.repeat(Math.round(p.ratings.average))}{'☆'.repeat(5 - Math.round(p.ratings.average))}
                    <span style={{ color: '#9CA3AF', marginLeft: '3px' }}>({p.ratings.count})</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ProductRecommendations;
